import { useState, ReactNode } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Quote = {
  id: number
  number: string
  partner: number
  partner_name?: string
  quote_date: string
  valid_until: string | null
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED'
  currency_code: string
  total: string
  converted_to_order: number | null
  converted_to_order_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  partner: number | 0
  quote_date: string
  valid_until: string | null
  currency_code: string
}

const STATUS_COLOR: Record<Quote['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-amber-100 text-amber-800',
}

const STATUS_FILTERS: ('ALL' | Quote['status'])[] = [
  'ALL', 'DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED',
]

export default function SalesQuotes() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const quotesQ = useQuery({
    queryKey: ['sales', 'quotes'],
    queryFn: async () => (await api.get<Quote[]>('/sales/quotes/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Quote['status']>('ALL')

  function openCreate() {
    setDraft({
      ...emptyDraft(),
      number: `QUO-${Date.now().toString().slice(-6)}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Quote>('/sales/quotes/', {
      ...draft,
      partner: Number(draft.partner),
      status: 'DRAFT',
      notes: '',
      lines: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      toast.success('Quote created')
      setCreating(false)
      navigate(`/admin/sales/quotes/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/sales/quotes/${id}/`),
    onSuccess: () => {
      toast.success('Quote deleted')
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/sales/quotes/${id}/convert_to_order/`)).data,
    onSuccess: (data: any) => {
      toast.success('Converted to order')
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      if (data?.id) navigate(`/admin/sales/orders/${data.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const quotes = quotesQ.data || []
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const filtered = filterStatus === 'ALL'
    ? quotes
    : quotes.filter((q) => q.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Quotes</h2>
          <p className="text-sm text-muted-foreground">
            {quotes.length} total · {quotes.filter((q) => q.status === 'SENT' || q.status === 'ACCEPTED').length} in flight
          </p>
        </div>
        <Button onClick={openCreate} disabled={partners.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New quote
        </Button>
      </div>

      {partners.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>customer</strong> under Partners before creating quotes.
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {quotesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No quotes.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th>Converted to</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((q) => (
                <tr
                  key={q.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/sales/quotes/${q.id}`)}
                >
                  <Td className="font-mono text-xs">{q.number}</Td>
                  <Td className="text-xs">{q.quote_date}</Td>
                  <Td>{q.partner_name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[q.status]}`}>{q.status}</span>
                  </Td>
                  <Td className="text-xs">
                    {q.converted_to_order_number ? (
                      <Link
                        to={`/admin/sales/orders/${q.converted_to_order}`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1 font-mono"
                      >
                        <ArrowRight className="h-3 w-3" /> {q.converted_to_order_number}
                      </Link>
                    ) : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/sales/quotes/${q.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {q.status === 'ACCEPTED' && !q.converted_to_order && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Convert quote ${q.number} to a sales order`,
                              () => convertMut.mutate(q.id),
                            )}
                            disabled={convertMut.isPending}
                            className="text-green-700"
                          >
                            Convert to order
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!q.converted_to_order}
                          onClick={() => !q.converted_to_order && confirm(
                            `quote ${q.number}`,
                            () => deleteMut.mutate(q.id),
                          )}
                          className={!q.converted_to_order ? 'text-destructive' : ''}
                        >
                          {q.converted_to_order ? 'Delete (converted)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Lines + notes filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New quote"
        description="Create the quote header. Add line items, notes, and convert to order on the record page once the customer accepts."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="q-num">Number *</FieldLabel>
              <Input id="q-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="q-cur">Currency</FieldLabel>
              <Input id="q-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="q-customer">Customer *</FieldLabel>
            <SearchableSelect
              id="q-customer"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={partners.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="q-date">Quote date *</FieldLabel>
              <DatePicker id="q-date" required value={draft.quote_date}
                onChange={(v) => setDraft({ ...draft, quote_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="q-valid">Valid until</FieldLabel>
              <DatePicker id="q-valid" value={draft.valid_until || ''}
                onChange={(v) => setDraft({ ...draft, valid_until: v || null })} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    partner: 0,
    quote_date: localISODate(new Date()),
    valid_until: null,
    currency_code: 'EUR',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
